import { makeStyles } from '@material-ui/core/styles';

import { RiDeleteBinLine, RiMore2Fill } from '@remixicon/react';

import { ButtonIcon, Menu, MenuItem, MenuTrigger, Text } from '@backstage/ui';

import {
  extractLastNameComponent,
  pathToModuleStyle,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';

import {
  DEFAULT_ARTIFACT_ICON,
  useArtifactExtensions,
} from '../../extensions/artifact-extension';
import { ArtifactListItem } from '../ArtifactListItem';
import { GradientIcon } from '../GradientIcon/GradientIcon';

const useStyles = makeStyles({
  iconSlot: {
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    '& > svg': {
      height: 16,
      width: 16,
    },
  },
  primaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface DefaultArtifactListItemProps {
  artifact: AttachmentFragment;
  resolvedDisplayName?: string;
  onSelect: () => void;
  onDelete?: () => void;
}

export const DefaultArtifactListItem = ({
  artifact,
  resolvedDisplayName,
  onSelect,
  onDelete,
}: DefaultArtifactListItemProps) => {
  const classes = useStyles();
  const extensions = useArtifactExtensions();
  const ext = extensions.find(e => e.predicate(artifact));
  const { Icon, color: backgroundColor } =
    ext?.icon?.(artifact) ?? DEFAULT_ARTIFACT_ICON;
  const typeLabel =
    pathToModuleStyle(artifact.uri)?.label ?? ext?.label ?? 'Link';
  const fallbackName = extractLastNameComponent(artifact.uri);
  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ArtifactListItem.Root onClick={onSelect}>
      <ArtifactListItem.Icon>
        <GradientIcon from={backgroundColor} to={`${backgroundColor}88`}>
          <span className={classes.iconSlot}>
            <Icon size={16} />
          </span>
        </GradientIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" className={classes.primaryText}>
            {artifact.displayName || resolvedDisplayName || fallbackName}
          </Text>
        }
        secondary={
          <Text
            variant="body-x-small"
            as="div"
            color="secondary"
            className={classes.secondaryText}
          >
            {typeLabel} &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
          />
          <Menu placement="bottom end">
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};
